import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    Chip,
    Paper,
    Tabs,
    Tab,
    Button,
    useTheme,
    useMediaQuery,
    IconButton,
    CircularProgress
} from '@mui/material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { IoIosArrowRoundDown } from "react-icons/io";
import { FaFileDownload } from "react-icons/fa";
import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6"

import fullLogoBlack from '../assets/pathomiq-ai-logo-black.webp'
import { SegmentedMeter } from '../component/report/SegmentedMeter';
import { useParams } from 'react-router';
import { fetchGet, get } from '../utils/http';
import { LoadingAnimeSpinner } from '../component/common/Loaders';
import { SlideView } from '../component/report/SlideViewe';
import { ImageReport } from '../utils/types'


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

export interface TreatmentResponseData {
    lowPRAD_RP: number;
    lowPRAD_Adj: number;
    highPRAD_RP: number;
    highPRAD_Adj: number;
}

const EndpointRiskMeter = ({
    title,
    risk,
    threshold
}: {
    title: string;
    risk: number;
    threshold: number;
}) => {
    const riskPercent = risk * 100
    const thresholdPercent = threshold * 100
    const level = riskPercent > thresholdPercent ? 'HIGH' : 'LOW'
    
    return (
        <Card className="shadow-sm h-100" sx={{ borderRadius: { xs: 2, md: 3 } }}>
            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                <Box className="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center mb-2 gap-2">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.25rem' } }}>
                        {title}
                    </Typography>
                    <Chip
                        label={`Patient Risk: ${riskPercent}% (${level})`}
                        sx={{ fontWeight: 700, fontSize: { xl: '1rem' }, bgcolor: riskPercent < thresholdPercent ? '#1b5e20' : '#b71c1c', color: '#fff' }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" className="mb-4" sx={{ fontSize: { xl: '1rem' } }}>
                    High-risk reporting threshold: &gt;{thresholdPercent}% at 5 years post-diagnostic.
                </Typography>

                <Box className="p-3 pt-5 rounded-3" sx={{ bgcolor: '#fafafa', border: '1px solid #f0f0f0' }}>
                    {/* Main Bar Container */}
                    <Box sx={{ position: 'relative', height: { xs: 28, md: 48, xl: 64 }, bgcolor: '#ef9a9a', overflow: 'visible' }}>
                        {/* Low Risk Zone (Blue as per document) */}
                        <Box sx={{ position: 'absolute', left: 0, width: `${thresholdPercent}%`, height: '100%', bgcolor: '#81c784' }} />
                        {/* Threshold Dashed Line */}
                        <Box sx={{
                            position: 'absolute',
                            left: `${thresholdPercent}%`,
                            height: '100%',
                            width: '1px',
                            borderLeft: '1.5px dashed #111',
                            zIndex: 3
                        }}>
                            <Typography variant="caption" sx={{ position: 'absolute', bottom: -20, left: -10, fontWeight: 700 }}>
                                {thresholdPercent}%
                            </Typography>
                        </Box>

                        {/* 25%, 50%, 75% Grid Lines */}
                        {[25, 50, 75].map(mark => (
                            <Box
                                key={mark}
                                sx={{
                                    position: 'absolute',
                                    left: `${mark}%`,
                                    height: '100%',
                                    width: '1px',
                                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                                    zIndex: 1
                                }}
                            />
                        ))}

                        {/* Pointer */}
                        <Box sx={{
                            position: 'absolute',
                            left: `calc(${riskPercent}% - 16px)`,
                            top: -32,
                            height: '100%',
                            color: riskPercent < thresholdPercent ? '#1b5e20' : '#b71c1c',
                        }}>
                            <IoIosArrowRoundDown  size={36} />
                        </Box>
                        
                        {/* Marker */}
                        <Box sx={{
                            position: 'absolute',
                            left: `${riskPercent}%`,
                            height: '100%',
                            width: '4px',
                            bgcolor: riskPercent < thresholdPercent ? '#1b5e20' : '#b71c1c',
                            boxShadow: '0 0 6px rgba(0,0,0,0.5)' 
                        }} />
                    </Box>

                    {/* 0, 25, 50, 75, 100 Scale Labels */}
                    <Box sx={{ position: 'relative', height: '24px', mt: 3 }}>
                        {[0, 25, 50, 75, 100].map(val => (
                            <Typography
                                key={val}
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    position: 'absolute',
                                    left: `${val}%`,
                                    transform: val === 0 ? 'translateX(0)' : val === 100 ? 'translateX(-100%)' : 'translateX(-50%)',
                                    fontSize: { xl: '0.9rem' }
                                }}
                            >
                                {val}%
                            </Typography>
                        ))}
                    </Box>

                </Box>
            </CardContent>
        </Card>
    )
}

export default function ResponsiveProstateReportDashboard() {
    const theme = useTheme();

    const { imageId } = useParams()

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [imageReport, setImageReport] = useState<ImageReport | null>(null)
    const [activeTab, setActiveTab] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloadOpen, setDownloadOpen] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const reportData: any = {
        patient: {
            name: "John Doe",
            dob: "01/01/1955",
            mrn: "#001"
        },
        physician: {
            name: "Jane Doe, MD",
            practice: "Cancer Care Center",
            reportDate: "01/03/2025"
        },
        specimen: {
            type: "Radical prostatectomy",
            orderDate: "01/01/2025",
            accession: "#1234"
        },
        status: {
            status: "Final Draft",
            issued: "01/03/2025",
            purpose: "Not for clinical use"
        },
        findings: {
            pathologicStage: "pT2",
            marginStatus: "Positive",
            recentPSA: "5.0 ng/mL",
            gleasonScore: "3+4",
            nccnRiskCategory: "Intermediate Risk"
        },
        riskEstimates: {
            bcr: { risk: 0.3, threshold: 0.1 },
            metastasis: { risk: 0.02, threshold: 0.05 },
        },
        validationCohort: {
            bcr: { rp_data: [25, 78], adj_data: [15, 22] },
            metastasis: { rp_data: [10, 48], adj_data: [6, 12] }
        },
        populationPercentiles: {
            bcr: { percentile: 62 },
            metastasis: { percentile: 8 }
        },
        clinicalInterpretation: {
            evidence: [
                "Independent post-prostatectomy validation2,3 found PATHOMIQ_PRAD to be associated with biochemical recurrence and metastasis, providing information beyond routine clinicopathologic variables.",
                "Patients with high estimated risk benefited from early treatment intensification.",
                "The validation cohort comprised XX men with clinically localized prostate cancer who underwent radical prostatectomy; the published study reported biochemical recurrence and metastasis outcomes.",
                "The classifier uses routine digitized H&E whole-slide images and evaluates tumor architecture, epithelium, and tumor-microenvironment morphology to generate estimated risk.",
                "A high risk is prognostic information. It is not, by itself, evidence that a specific adjuvant treatment will benefit an individual patient."
            ],
            supported: [
                "Risk stratification after radical prostatectomy",
                "Adjunct to standard clinical and pathology factors",
                "Supports multidisciplinary tumor board discussion"
            ],
            notEstablished: [
                "A diagnosis of recurrent or metastatic disease",
                "A guaranteed event within a fixed time",
                "A directive for a particular therapy"
            ]
        },
        limitations: [
            "Performance may vary with tissue quality, slide preparation, scanner characteristics, tumor sampling, and patient population.",
            "A prognostic association does not establish that the reported event will occur in an individual patient.",
            "The result does not replace pathologist review, postoperative PSA monitoring, imaging, clinical guidelines, or multidisciplinary judgment.",
            "Treatment-benefit claims require separate predictive validation. Do not infer benefit from radiotherapy, ADT, or another therapy solely from this risk."
        ],
        references: [
            { id: 1, citation: "Fay M, et al. Artificial Intelligence-Based Digital Histologic Classifier for Prostate Cancer Prognosis. JCO Clin Cancer Inform. 2025.", doi: "10.1200/CCI-24-00292", pmid: "40532127" },
            { id: 2, citation: "Huang W, et al. A Novel Artificial Intelligence-Powered Method for Prediction of Early Recurrence of Prostate Cancer. JCO Clin Cancer Inform. 2022;6:e2100131.", doi: "10.1200/CCI.21.00131" },
            { id: 3, citation: "PATHOMIQ_PRAD Source Report. Draft morphology-based radical-prostatectomy prostate risk classifier, 2025." },
            { id: 4, citation: "Nair SS, et al. A Novel Artificial Intelligence-powered Tool for Precise Risk Stratification of Prostate Cancer Progression. Eur Urol. 2025;87(6):728-729.", pmid: "39232981" },
            { id: 5, citation: "Huang W, et al. Development and Validation of an Artificial Intelligence-Powered Platform for Prostate Cancer Grading and Quantification. JAMA Netw Open. 2021;4(11):e2132554." },
            { id: 6, citation: "Feltes Benitez N, et al. Prognostic Factors Associated with Biochemical Relapse After Radiotherapy in Localized Prostate Cancer. Biomedicines. 2025;13(9):2185.", pmid: "41007748" },
            { id: 7, citation: "Clarke, H. BCR within 5 years is prognostic for metastasis, prostate cancer-specific mortality. Urology Times. Oct 2024." }
        ]
    }

    const getImageData = async () => {
        setLoading(true)
        try {
            const res = await get(`Reports/data/${imageId}`)
            setImageReport(res)
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getImageData()
    }, [])  // eslint-disable-line

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const buildChartData = (rp_data: number[], adj_data: number[]) => ({
        labels: ['Low PRAD', 'High PRAD'],
        datasets: [
            {
                label: 'RP Alone',
                data: rp_data,
                backgroundColor: '#a93226',
                borderRadius: 4,
            },
            {
                label: 'Adjuvant Treatment',
                data: adj_data,
                backgroundColor: '#2e5b88',
                borderRadius: 4,
            }
        ]
    });

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: { boxWidth: 12, font: { size: isMobile ? 10 : 12 } }
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => `${context.dataset.label}: ${context.raw}%`
                }
            }
        },
        scales: {
            y: { min: 0, max: 100, title: { display: !isMobile, text: 'Estimated Risk (%)' } }
        }
    };


    const toggleDownload = () => setDownloadOpen(prevState => !prevState)

    const downloadReport = async () => {
        setDownloading(true)
        try {
            if (!imageReport) throw new Error('No Report data')

            const res = await fetchGet(`Reports/get-pdf/${imageReport.id}`);
            if (!res.ok) throw new Error('Failed to fetch file');
                        
            let fileName = imageReport.name?.split('/').pop() || `slide_${imageReport.id}`;
            const disposition = res.headers.get('Content-Disposition');
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename="?([^";]+)"?/);
                if (match && match[1]) fileName = match[1];
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.error(err)
            alert('Could not download the report PDF. Please try again.');
        } finally {
            setDownloading(false)
            setDownloadOpen(false)
        }
    }

    return (
        <Box sx={{ bgcolor: '#f4f6f9', minHeight: '100vh', pb: { xs: 4, md: 6 } }}>
            {loading
                ? <Box className='position-absolute h-100 w-100 d-flex justify-content-center align-items-center'>
                    <LoadingAnimeSpinner />
                </Box>
                : imageReport
                    ? <>
                    {/* Header */}
                    <Paper elevation={0} sx={{
                        borderBottom: '1px solid #e0e0e0',
                        bgcolor: '#ffffff',
                        py: { xs: 2, md: 3 },
                        px: { xs: 2, md: 3, xl: 5 },
                        mb: { xs: 2, md: 4 }
                    }}>
                        <Box className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                            <img src={fullLogoBlack} height={28} alt="PATHOMIQ AI"/>
                            <Typography variant="body2" color="text.secondary" className='text-uppercase' sx={{ fontSize: { xl: '1rem' } }}>
                                Prostate Cancer Risk Summary
                            </Typography>
                        </Box>
                    </Paper>

                    {/* Main Container - Expands for Ultrawide up to 1800px */}
                    <Box sx={{ maxWidth: { xs: '100%', xl: 1800 }, margin: '0 auto', px: { xs: 2, sm: 3, lg: 4, xl: 6 } }}>

                        {/* Patient & Order Metadata Bar */}
                        <Card className="shadow-sm mb-4" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                            <CardContent sx={{ py: { xs: 2, md: 2.5 } }}>
                                <Grid container spacing={{ xs: 2, md: 3, xl: 4 }}>
                                    <Grid item xs={12} sm={6} md={3} xl={3}>
                                        <Typography variant="caption" color="text.secondary" className="d-block text-uppercase fw-bold">Patient Info</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>{imageReport.patient_name || reportData.patient.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>DOB: {imageReport.patient_dob || reportData.patient.dob}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>MRN: {imageReport.patient_mrn || reportData.patient.mrn}</Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3} xl={3}>
                                        <Typography variant="caption" color="text.secondary" className="d-block text-uppercase fw-bold">Ordering Physician</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>{imageReport.physician_name || reportData.physician.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>{imageReport.physician_practice || reportData.physician.practice}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>Report date: {imageReport.physician_report_date || reportData.physician.reportDate}</Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3} xl={3}>
                                        <Typography variant="caption" color="text.secondary" className="d-block text-uppercase fw-bold">Specimen</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>
                                            {imageReport.slide_type.toLowerCase() === 'rp' ? 'Radical prostatectomy' : 'Biopsy'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>Accession: {imageReport.specimen_accession || reportData.specimen.accession}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>Ordered: {imageReport.specimen_order_date || reportData.specimen.orderDate}</Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3} xl={3}>
                                        <Typography variant="caption" color="text.secondary" className="d-block text-uppercase fw-bold">Report Status</Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>{imageReport.status || reportData.status.status}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>Issued: {imageReport.issued || reportData.status.issued}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xl: '1rem' } }}>Purpose: {imageReport.purpose || reportData.status.purpose}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Tab Navigation */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, md: 4 } }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                textColor="primary"
                                indicatorColor="primary"
                                sx={{'& .MuiTab-root': {
                                    fontWeight: 700,
                                    fontSize: { xs: '0.85rem', md: '0.95rem', xl: '1.1rem' },
                                    textTransform: 'none'
                                }}}
                            >
                                <Tab label="PRAD Risk Profile" />
                                <Tab label="Validation Cohorts & Pathology" />
                                <Tab label="Clinical Guidance" />
                            </Tabs>
                        </Box>

                        {/* PAGE 1 */}
                        {activeTab === 0 && (
                            <Box className="fade-in">
                                <Typography variant="h6" className="mb-3" sx={{ fontWeight: 700, color: '#2c3e50', fontSize: { xl: '1.4rem' } }}>
                                    Prostate Cancer Endpoint Risk Assessment (5 Years)
                                </Typography>

                                <Grid container spacing={{ xs: 2, md: 3, xl: 4 }}>

                                    {/* BCR Risk Card */}
                                    <Grid item xs={12} lg={6} xl={6}>
                                        <EndpointRiskMeter
                                            title="Risk of biochemical recurrence (BCR)"
                                            risk={imageReport.bcr_risk || reportData.riskEstimates.bcr.risk}
                                            threshold={imageReport.bcr_risk_threshold || reportData.riskEstimates.bcr.threshold}
                                        />
                                    </Grid>

                                    {/* Metastasis Risk Card */}
                                    <Grid item xs={12} lg={6} xl={6}>
                                        <EndpointRiskMeter 
                                            title="Risk of metastasis"
                                            risk={imageReport.metastasis_risk || reportData.riskEstimates.metastasis.risk}
                                            threshold={imageReport.metastasis_risk_threshold || reportData.riskEstimates.metastasis.threshold}
                                        />
                                    </Grid>

                                    {/* Patient Findings Grid */}
                                    <Grid item xs={12}>
                                        <Card className="shadow-sm" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, fontSize: { xl: '1.25rem' } }}>
                                                    Clinical & Pathologic Profile Overview
                                                </Typography>
                                                <Grid container spacing={{ xs: 1.5, md: 2, xl: 3 }}>
                                                    {[
                                                        { label: 'Pathologic Stage', value: imageReport.pathological_stage || reportData.findings.pathologicStage, color: 'text.primary' },
                                                        { label: 'Surgical Margin', value: imageReport.surgical_margin_status || reportData.findings.marginStatus, color: reportData.findings.marginStatus.toLowerCase() === 'positive' ? '#d32f2f' : 'text.primary' },
                                                        { label: 'Recent PSA', value: imageReport.psa || reportData.findings.recentPSA, color: 'text.primary' },
                                                        { label: 'Gleason Score', value: imageReport.gleason_grade || reportData.findings.gleasonScore, color: 'text.primary' }
                                                    ].map((item, idx) => (
                                                        <Grid item xs={6} sm={3} key={idx}>
                                                            <Paper elevation={0} sx={{ p: { xs: 1.5, xl: 3 }, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef', height: '100%' }}>
                                                                <Typography variant="caption" color="text.secondary" className="d-block" sx={{ fontSize: { xl: '1rem' } }}>{item.label}</Typography>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, fontSize: { xs: '1.1rem', md: '1.25rem', xl: '1.5rem' } }}>{item.value}</Typography>
                                                            </Paper>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Baseline risk classification */}
                                    <Grid item xs={12}>
                                        <Card className="shadow-sm" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, fontSize: { xl: '1.25rem' } }}>
                                                    Baseline Risk Classification
                                                </Typography>
                                                <Paper elevation={0} sx={{ p: { xs: 1.5, xl: 3 }, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef', height: '100%' }}>
                                                    <Typography variant="caption" color="text.secondary" className="d-block" sx={{ fontSize: { xl: '1rem' } }}>NCCN risk category</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem', xl: '1.5rem' } }}>{}</Typography>
                                                </Paper>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* PAGE 2 */}
                        {activeTab === 1 && (
                            <Box className="fade-in">
                                <Grid container spacing={{ xs: 2, md: 3, xl: 4 }}>
                                    {/* Heatmap Viewer */}
                                    <Grid item xs={12} lg={5} xl={4}>
                                        <Card className="shadow-sm h-100" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Box className="d-flex justify-content-between align-items-center mb-2">
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.25rem' } }}>Digital Pathology Heatmap</Typography>
                                                    <Button href={`/viewer/${imageId}`} size="small" variant="outlined" sx={{ fontSize: { xl: '0.9rem' } }} target="_blank">
                                                        Open Viewer
                                                    </Button>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" className="mb-3" sx={{ fontSize: { xl: '1rem' } }}>
                                                    Estimated Gleason grades and outcome-associated architectural microenvironments.
                                                </Typography>

                                                <SlideView slideId={imageReport!.image_unique_id} />
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Percentiles */}
                                    <Grid item xs={12} lg={7} xl={8}>
                                        <Card className="shadow-sm h-100" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: { xl: '1.25rem' } }}>Population Relative Risk</Typography>
                                                <Typography variant="body2" color="text.secondary" className="mb-4" sx={{ fontSize: { xl: '1rem' } }}>
                                                    The following example illustrates the patient's relative risk placement within the validation cohort distribution for the specific endpoints. It characterizes relative standing for this patient among the patient population who undergo first-line of definitive therapy (RP/RT), noting that nearly 20-30% of prostate cancer patients in this cohort undergo disease progression either BCR or metastasis within 5 years of the first line of therapy.
                                                </Typography>

                                                {[
                                                    { title: 'BCR Risk - Population percentile', val: imageReport.bcr_population_percentile || reportData.populationPercentiles.bcr.percentile },
                                                    { title: 'Metastasis Risk - Population percentile', val: imageReport.metastasis_population_percentile || reportData.populationPercentiles.metastasis.percentile }
                                                ].map((meter, idx) => (
                                                    <Box key={idx} className="d-flex flex-column gap-4">
                                                        <SegmentedMeter label={meter.title} percentile={meter.val} />
                                                    </Box>
                                                ))}
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Charts */}
                                    <Grid item xs={12}>
                                        <Card className="shadow-sm" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Box className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center mb-2 gap-2">
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xl: '1.25rem' } }}>
                                                        Validation-Cohort Treatment Response
                                                    </Typography>
                                                    <Chip label="Observational Cohort Data" size={isMobile ? "small" : "medium"} variant="outlined" sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }} />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" className="mb-4" sx={{ fontSize: { xl: '1rem' } }}>
                                                    Estimated outcome risk stratified by PRAD risk group (Low vs. High) and by treatment status (radiation alone vs. adjuvant ADT) following RP relevant for this patient.
                                                </Typography>

                                                <Grid container spacing={{ xs: 2, md: 4, xl: 5 }}>
                                                    <Grid item xs={12} lg={6}>
                                                        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2, xl: 3 }, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                            <Box className="d-flex justify-content-between align-items-center mb-2">
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xl: '1.1rem' } }}>Biochemical Recurrence (BCR)</Typography>
                                                            </Box>
                                                            <Box sx={{ height: { xs: 220, sm: 260, md: 300, xl: 400 }, width: '100%' }}>
                                                                <Bar data={
                                                                    buildChartData(
                                                                        imageReport.bcr_rp_alone || reportData.validationCohort.bcr.rp_data,
                                                                        imageReport.bcr_adjuvant || reportData.validationCohort.bcr.adj_data
                                                                    )} 
                                                                    options={chartOptions}
                                                                />
                                                            </Box>
                                                        </Paper>
                                                    </Grid>

                                                    <Grid item xs={12} lg={6}>
                                                        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2, xl: 3 }, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                                            <Box className="d-flex justify-content-between align-items-center mb-2">
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xl: '1.1rem' } }}>Metastasis</Typography>
                                                            </Box>
                                                            <Box sx={{ height: { xs: 220, sm: 260, md: 300, xl: 400 }, width: '100%' }}>
                                                                <Bar data={
                                                                    buildChartData(
                                                                        imageReport.metastasis_rp_alone || reportData.validationCohort.metastasis.rp_data,
                                                                        imageReport.metastasis_adjuvant || reportData.validationCohort.metastasis.adj_data
                                                                    )} 
                                                                    options={chartOptions}
                                                                />
                                                            </Box>
                                                        </Paper>
                                                    </Grid>
                                                </Grid>

                                                <Typography variant="caption" paragraph color="text.secondary" sx={{ bgcolor: '#f8f9fa', p: 1, fontSize: { xl: '1.1rem' }, lineHeight: { xl: 1.7 }, mt: '1rem' }}>
                                                    Note: these risk estimates are derived from an observational cohort study and are adjusted for treatment assignment (e.g., inverse-probability-of-treatment weighting), not from a randomized controlled trial. Differences between the radiation-alone and adjuvant-ADT groups may reflect residual confounding and should not be interpreted as a direct causal treatment effect.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* PAGE 3 */}
                        {activeTab === 2 && (
                            <Box className="fade-in">
                                <Grid container spacing={{ xs: 2, md: 3, xl: 4 }}>
                                    <Grid item xs={12} lg={8} xl={8}>
                                        <Card className="shadow-sm mb-3" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: { xl: '1.25rem' } }}>Clinical Interpretation</Typography>
                                                <Typography variant="body2" paragraph color="text.secondary" sx={{ bgcolor: '#f8f9fa', p: 1, fontSize: { xl: '1.1rem' }, lineHeight: { xl: 1.7 } }}>
                                                    The result identifies a high-risk morphology profile after radical prostatectomy (RP), relevant to considering adjuvant therapy. Consider the risk alongside PSA kinetics, pathologic stage, margin status, grade group, nodal status, imaging, comorbidities, patient preferences, and applicable guidelines. The report does not independently prescribe radiotherapy, androgen-deprivation therapy, or another treatment.
                                                </Typography>
                                                <Divider className="my-3" />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xl: '1.15rem' } }}>Clinical evidence relevant to this result:</Typography>
                                                <Box className='d-flex flex-column gap-2 p-3 pb-0'>
                                                    {reportData.clinicalInterpretation.evidence.map((i: string, idx: number) => (
                                                        <li key={idx}><Typography variant="body2" sx={{ fontSize: { xl: '1.05rem' } }}>{i}</Typography></li>
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>

                                        <Card className="shadow-sm" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, fontSize: { xl: '1.25rem' } }}>Intended Use &amp; Limitations</Typography>
                                                <Typography variant="body2" paragraph color="text.secondary" sx={{ bgcolor: '#f8f9fa', p: 1, fontSize: { xl: '1.1rem' }, lineHeight: { xl: 1.7 } }}>
                                                    PATHOMIQ Prostate Classifier is intended for use in patients diagnosed with localized or regional prostate cancer who have not received pelvic radiation or hormone therapy prior to the biopsy or prostatectomy. The PATHOMIQ_PRAD risk is intended for use as an adjunct to conventional clinical risk factors for determining the biochemical recurrence or metastatic potential of the tumor and patient prognosis.
                                                </Typography>
                                                <Divider className="my-3" />
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: { xl: '1.15rem' } }}>Limitations:</Typography>
                                                <Box className='d-flex flex-column gap-2 p-3 pb-0'>
                                                    {reportData.limitations.map((i: string, idx: number) => (
                                                        <li key={idx}><Typography variant="body2" sx={{ fontSize: { xl: '1rem' } }}>{i}</Typography></li>
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4} xl={4}>
                                        <Card className="shadow-sm mb-3" sx={{ borderRadius: { xs: 2, md: 3 }, background: '#2e7d32', color: 'white' }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Box className='d-flex gap-1 align-items-center'>
                                                    <IoMdCheckmarkCircle size={28} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>Supported Interpretation</Typography>
                                                </Box>
                                                <Box className='d-flex flex-column gap-2 p-3 pb-0'>
                                                    {reportData.clinicalInterpretation.supported.map((i: string, idx: number) => (
                                                        <li key={idx}><Typography variant="body2" sx={{ fontSize: { xl: '1.05rem' } }}>{i}</Typography></li>
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>

                                        <Card className="shadow-sm mb-3" sx={{ borderRadius: { xs: 2, md: 3 }, background: '#c62828', color: 'white' }}>
                                            <CardContent sx={{ p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Box className='d-flex gap-1 align-items-center'>
                                                    <IoMdCloseCircleOutline size={28} />
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xl: '1.15rem' } }}>Not Established By This Risk Alone</Typography>
                                                </Box>
                                                <Box className='d-flex flex-column gap-2 p-3 pb-0'>
                                                    {reportData.clinicalInterpretation.notEstablished.map((i: string, idx: number) => (
                                                        <li key={idx}><Typography variant="body2" sx={{ fontSize: { xl: '1.05rem' } }}>{i}</Typography></li>
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>

                                        <Card className="shadow-sm" sx={{ borderRadius: { xs: 2, md: 3 } }}>
                                            <CardContent sx={{ overflowY: 'auto', p: { xs: 2, md: 3, xl: 4 } }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: { xl: '1.15rem' } }}>References</Typography>
                                                {reportData.references.map((ref: any) => (
                                                    <Box key={ref.id} className="mb-3">
                                                        <Typography variant="caption" className="d-block" sx={{ lineHeight: 1.4, fontSize: { xl: '0.95rem' } }}>
                                                            <strong>[{ref.id}]</strong> {ref.citation}
                                                        </Typography>
                                                        {ref.doi && <Typography variant="caption" color="primary" className="d-block" sx={{ fontSize: { xl: '0.9rem' } }}>DOI: {ref.doi}</Typography>}
                                                    </Box>
                                                ))}
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                </Grid>
                            </Box>
                        )}
                    </Box>

                    <Box className='shadow-sm d-flex gap-1 align-items-center' sx={{
                        position: 'fixed',
                        bottom: '2.5rem',
                        right: 0,
                        zIndex: 2,
                        bgcolor: '#ffffff',
                        borderRadius: '100px 0 0 100px',
                        p: 1,
                        color: 'grey',
                        transform: downloadOpen ? 'translateX(0%)' : 'translateX(50%)'
                    }}>
                        {downloading
                            ? <CircularProgress size={39} color='inherit' />
                            : <>
                                <IconButton onClick={toggleDownload} aria-label='toggle download option'>
                                    {downloadOpen ? <FaAnglesRight /> : <FaAnglesLeft />}
                                </IconButton>
                                <IconButton onClick={downloadReport} aria-label='Download as PDF'>
                                    <FaFileDownload/>
                                </IconButton>
                            </>
                        }
                    </Box>
                    </>
                : <Box className='position-absolute w-100 h-100 d-flex justify-content-center align-items-center'>
                    <Typography variant='h5' component='h1'>Report data not found</Typography>
                </Box>
            }
        </Box>
    );
}